import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import useDocumentTitle from "../../utils/useDocumentTitle";

import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import EditIcon from "@material-ui/icons/Edit";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import PageContent from "../../components/PageContent";
import { Pagination } from "../../components/Pagination";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { TableConfig } from "../../config/Config";
import {
  AdminRole,
  IsCampaignAdmin,
  IsCampaignManager,
  IsCOEAdmin,
  IsCoordinator,
  IsSuperAdmin,
  IsSupportAdmin,
  Role,
} from "../../config/Roles";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import styles from "./Settings.module.scss";

import { useTranslation } from "react-i18next";
import CampaignFilter from "../../components/CampaignFilter";
import filterStyles from "../../components/CampaignFilter/index.module.scss";
import GetAllCountries from "../../components/GetAllCountries";
import { PerPage } from "../../components/PerPage";
import Search from "../../components/Search";
import Select from "../../components/Select";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { utcToLocal } from "../../utils/datetime";

dayjs.extend(utc);

export function UserManagement(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const history = useHistory();
  const { addNotification } = useNotification();
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [offset, setOffset] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(TableConfig.resultPerPage);
  const [columnName, setColumnName] = useState("u_fullname");
  const [sortOrder, setSortOrder] = useState("asc");
  const [uid] = useState(getLocalValue("uid"));
  const [searchKey, setSearchKey] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedAdminRole, setSelectedAdminRole] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const roleFilterOptions = [
    { value: "all", label: t("userManagement.allRoles") },
    { value: Role.COACH, label: "Coach" },
    { value: Role.COACHEE, label: "Coachee" },
  ];

  const adminRoleFilterOptions = [
    { value: "all", label: t("userManagement.allAdminRoles") },
    { value: 0, label: "Normal User" },
    { value: AdminRole.CAMPAIGN_MANAGER, label: "Functional Unit Manager" },
    { value: AdminRole.COORDINATOR, label: "Coordinator" },
    { value: AdminRole.COE_ADMIN, label: "COE" },
    { value: AdminRole.EXECUTIVE, label: "Executive" },
    { value: AdminRole.SUPPORT_ADMIN, label: "Support Admin" },
    { value: AdminRole.SUPER_ADMIN, label: "Super Admin" },
    { value: AdminRole.IT_MANAGER, label: "IT Manager" },
    { value: AdminRole.HR, label: "HR" },
    { value: AdminRole.CEO, label: "CEO" },
  ];

  const statusFilterOptions = [
    { value: "all", label: t("userManagement.allStatuses") },
    { value: "New", label: "New" },
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const tableHeader = [
    { name: "u_fullname", label: t("userManagement.name"), width: "20%" },
    { name: "employee_id", label: t("userManagement.id"), width: "10%" },
    {
      name: "u_department",
      label: t("userManagement.department"),
      width: "10%",
    },
    { name: "u_country", label: t("userManagement.country"), width: "10%" },
    {
      name: "u_designation",
      label: t("userManagement.designation"),
      width: "12%",
    },
    { name: "u_role", label: t("userManagement.role"), width: "8%" },
    { name: "u_status", label: t("userManagement.status"), width: "7%" },
    { name: "created_datetime", label: t("userManagement.createdDate") },
    { name: "first_login_updated_at", label: t("userManagement.firstLogin") },
    { name: "last_login_datetime", label: t("userManagement.lastLogin") },
  ];

  const campaignManagerTableHeader = [
    { name: "u_fullname", label: t("userManagement.name"), width: "10%" },
    { name: "employee_id", label: t("userManagement.id"), width: "10%" },
    {
      name: "u_department",
      label: t("userManagement.department"),
      width: "10%",
    },
    { name: "u_country", label: t("userManagement.country"), width: "10%" },
    {
      name: "u_designation",
      label: t("userManagement.designation"),
      width: "12%",
    },
    { name: "u_role", label: t("userManagement.role"), width: "8%" },
    { name: "u_status", label: t("userManagement.status"), width: "7%" },
    { name: "created_datetime", label: t("userManagement.createdDate") },
    { name: "first_login_updated_at", label: t("userManagement.firstLogin") },
    { name: "last_login_datetime", label: t("userManagement.lastLogin") },
  ];

  const supportAdminTableHeader = [
    { name: "u_fullname", label: "Name", width: "10%" },
    { name: "u_username", label: "Username", width: "10%" },
    { name: "employee_id", label: "Id", width: "10%" },
    { name: "u_department", label: "Department", width: "10%" },
    { name: "u_country", label: "Country", width: "10%" },
    { name: "u_designation", label: "Designation", width: "12%" },
    { name: "u_role", label: "Role", width: "8%" },
    { name: "role", label: "Admin Role", width: "8%" },
    { name: "primary_email", label: "Primary Email", width: "8%" },
    { name: "u_status", label: "Status", width: "7%" },
    { name: "created_datetime", label: t("userManagement.createdDate") },
    { name: "first_login_updated_at", label: t("userManagement.firstLogin") },
    { name: "last_login_datetime", label: t("userManagement.lastLogin") },
  ];

  const [tableHeaderObj, setTableHeaderObj] = useState(tableHeader);

  //ascending
  //descending

  useEffect(() => {
    if (IsCampaignAdmin()) {
      setTableHeaderObj(campaignManagerTableHeader);
    }
  }, [IsCampaignAdmin()]);

  useEffect(() => {
    if (IsSupportAdmin() || IsSuperAdmin()) {
      setTableHeaderObj(supportAdminTableHeader);
    }
  }, [IsSuperAdmin(), IsSupportAdmin()]);

  useEffect(() => {
    // fetch from getSessionDetails api
    async function fetchData() {
      setIsLoading(true);
      let regionID = "";
      const campaignParam =
        selectedCampaign && selectedCampaign !== "all"
          ? `&campaign_id=${selectedCampaign}`
          : "";
      const roleParam =
        selectedRole !== "all" ? `&role=${selectedRole}` : "";
      const adminRoleParam =
        selectedAdminRole !== "all" ? `&admin_role=${selectedAdminRole}` : "";
      const statusParam =
        selectedStatus !== "all" ? `&status=${selectedStatus}` : "";
      let u_coaching_role = parseInt(getLocalValue("u_coaching_role_db"));
      if (IsSuperAdmin() || IsSupportAdmin()) {
        regionID = `&region_id=${selectedCountry === "all" ? 0 : selectedCountry}`;
      }
      const response = await apiRequest.get(
        `/api/getAllUsers?page=${offset}&limit=${perPage}&columnName=${columnName}&order=${sortOrder}&uid=${uid}&u_coaching_role=${u_coaching_role}&search=${searchKey}${campaignParam}${regionID}${roleParam}${adminRoleParam}${statusParam}`
      );

      if (response.status === 200) {
        const data = response.data;
        const userList = response.data.data;
        setUserData(userList);
        setTotalData(data.totalData);
      } else {
        addNotification(
          t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
          "error"
        );
      }
      setIsLoading(false);
    }

    fetchData();
  }, [
    offset,
    columnName,
    sortOrder,
    searchKey,
    selectedCampaign,
    perPage,
    selectedCountry,
    selectedRole,
    selectedAdminRole,
    selectedStatus,
  ]);

  useEffect(() => {
    setOffset(1);
  }, [selectedCampaign, selectedRole, selectedAdminRole, selectedStatus, selectedCountry]);

  const handlePaginationChange = offset => {
    setOffset(offset.selected + 1);
  };

  const sortData = name => {
    setColumnName(name);
    const order = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(order);
  };

  const handleDropdownChange = value => {
    setSelectedCampaign(value);
  };

  return (
    <DashboardLayout isDisableCampaignSelection={true}>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <SectionTitle>{t("leftNavigation.userManagement")}</SectionTitle>
            </div>
            <div className={styles.searchWrapper}>
              <div className={styles.searchField}>
                <Search 
                  handleSearch={val => {
                    setSearchKey(val);
                    setOffset(1);
                  }}
                />
              </div>
              <div className={styles.filtersGroup}>
                {(IsSupportAdmin() || IsSuperAdmin()) && (
                  <div className={styles.filterSelect}>
                    <GetAllCountries
                      countryChange={val => {
                        setSelectedCountry(val);
                      }}
                    />
                  </div>
                )}
                {(IsSupportAdmin() ||
                  IsSuperAdmin() ||
                  IsCampaignManager() ||
                  IsCoordinator() ||
                  IsCOEAdmin()) && (
                  <div className={styles.filterSelect}>
                    <CampaignFilter
                      showUserCampaignsOnly={!IsSupportAdmin()}
                      uid={uid}
                      handleCampaignChange={val => {
                        setSelectedCampaign(val);
                      }}
                    />
                  </div>
                )}
                {(IsSupportAdmin() ||
                  IsSuperAdmin() ||
                  IsCampaignManager() ||
                  IsCoordinator() ||
                  IsCOEAdmin()) && (
                  <>
                    <div className={styles.filterSelect}>
                      <Select
                        list={roleFilterOptions}
                        value={selectedRole}
                        className={filterStyles.toolBarDropDown}
                        variant="standard"
                        onChange={e => setSelectedRole(e.target.value)}
                      />
                    </div>
                    <div className={styles.filterSelect}>
                      <Select
                        list={adminRoleFilterOptions}
                        value={selectedAdminRole}
                        className={filterStyles.toolBarDropDown}
                        variant="standard"
                        onChange={e => setSelectedAdminRole(e.target.value)}
                      />
                    </div> 
                    <div className={styles.filterSelect}>
                      <Select
                        list={statusFilterOptions}
                        value={selectedStatus}
                        className={filterStyles.toolBarDropDown}
                        variant="standard"
                        onChange={e => setSelectedStatus(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <TableContainer component={Paper} elevation={0}>
              <Table aria-label="Team" className={styles.table}>
                <TableHead className={styles.tableHead}>
                  <TableRow>
                    {IsSuperAdmin() || IsSupportAdmin()
                      ? tableHeaderObj.map((tableHead, index) => (
                        <TableCell
                          key={index}
                          style={{ width: tableHead.width }}
                          className={styles.tableHeadCell}
                          onClick={() => {
                            sortData(tableHead.name);
                          }}
                        >
                          {tableHead.label}

                          {columnName === tableHead.name &&
                            sortOrder === "asc" && (
                              <span>
                                <KeyboardArrowUpIcon fontSize="small" />
                              </span>
                            )}
                          {columnName === tableHead.name &&
                            sortOrder === "desc" && (
                              <span>
                                <KeyboardArrowDownIcon fontSize="small" />
                              </span>
                            )}
                        </TableCell>
                      ))
                      : tableHeader.map((tableHead, index) => (
                        <TableCell
                          key={index}
                          style={{ width: tableHead.width }}
                          className={styles.tableHeadCell}
                          onClick={() => {
                            sortData(tableHead.name);
                          }}
                        >
                          {tableHead.label}

                          {columnName === tableHead.name &&
                            sortOrder === "asc" && (
                              <span>
                                <KeyboardArrowUpIcon fontSize="small" />
                              </span>
                            )}
                          {columnName === tableHead.name &&
                            sortOrder === "desc" && (
                              <span>
                                <KeyboardArrowDownIcon fontSize="small" />
                              </span>
                            )}
                        </TableCell>
                      ))}
                    <TableCell style={{ width: "3%" }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userData &&
                    userData.map((row, index) => (
                      <TableRow
                        key={index}
                        className={styles.tableRow}
                        onClick={() => {
                          history.push(
                            `/settings/userManagement/editUser/${row.uid}`
                          );
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.u_fullname}
                        </TableCell>
                        {IsSuperAdmin() | IsSupportAdmin() ? (
                          <TableCell>{row.u_username}</TableCell>
                        ) : (
                          ""
                        )}
                        <TableCell>{row.employee_id}</TableCell>
                        <TableCell>{row.u_department}</TableCell>
                        <TableCell>{row.u_country}</TableCell>
                        <TableCell>{row.u_designation}</TableCell>
                        <TableCell>{row.u_role_value}</TableCell>
                        {IsSuperAdmin() | IsSupportAdmin() ? (
                          <>
                            <TableCell>{row.role}</TableCell>
                            <TableCell>{row.primary_email}</TableCell>
                          </>
                        ) : (
                          ""
                        )}

                        <TableCell
                          className={
                            row.u_status === "New"
                              ? styles.statusNew
                              : row.u_status === "Active"
                                ? styles.statusActive
                                : styles.statusInactive
                          }
                        >
                          {row.u_status}
                        </TableCell>
                        <TableCell>
                          {row.created_datetime ? utcToLocal(row.created_datetime).format("DD MMM YYYY") : "-"}
                        </TableCell>
                        <TableCell>
                          {row.first_login_updated_at ? utcToLocal(row.first_login_updated_at).format("DD MMM YYYY HH:mm:ss") : "-"}
                        </TableCell>
                        <TableCell>
                          {row.last_login_datetime ? utcToLocal(row.last_login_datetime).format("DD MMM YYYY HH:mm:ss") : "-"}
                        </TableCell>
                        <TableCell>
                          <IconButton className={styles.editIcon} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  {_.isEmpty(userData) && (
                    <TableRow style={{ borderBottom: "1px solid #eeeeee" }}>
                      <TableCell style={{ borderBottom: 0 }} colSpan={6}>
                        {t("common.noRecordFound")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {totalData > perPage && (
              <div className={styles.footer}>
                <PerPage limit={perPage} setLimit={setPerPage} />
                <Pagination
                  totalData={totalData}
                  limit={perPage}
                  offset={offset}
                  handlePageClick={offset => {
                    handlePaginationChange(offset);
                  }}
                />
              </div>
            )}
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
